import {
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuGroup,
  MenuList,
  MenuOptionGroup,
  Stack,
  chakra,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, DeleteIcon, SearchIcon } from '@chakra-ui/icons';

import CustomIcon from './CheckboxIcon';
import React from 'react';
import { useSubscription } from '../hooks/useSubscription';

type STEP_NAME = 'SUBSCRIBE' | 'SUBSCRIBE_SUCCESS' | 'SUBSCRIBE_ERROR';

const SubmitButton = chakra(Button, {
  baseStyle: {
    background: '#1CA5CC',
    borderRadius: '6px',
    marginTop: '32px',
    color: 'white',
    ':hover': {
      background: '#60c0db',
    },
    ':hover[disabled]': {
      background: '#60c0db',
    },
  },
});

const StyledCheckbox = chakra(Checkbox, {
  baseStyle: {
    "span[class*='checkbox__control']:not([data-disabled])": {
      background: 'white',
      borderColor: '#9C9C9C',
      width: '23px',
      height: '23px',
      fontSize: '20px',
      _checked: {
        svg: {
          padding: '1px',
          width: '19px',
          height: '18px',
        },
      },
    },
  },
});

const StyledMenuButton = chakra(Button, {
  baseStyle: {
    background: 'transparent !important',
    textAlign: 'left',
    padding: 0,
    color: '#475467',
    borderBottom: '1px solid #E2E8F0',
    borderRadius: 0,
    ':hover': {
      background: 'transparent !important',
    },
    ':active': {
      background: 'transparent !important',
    },
    ':focus, :focus-visible': {
      borderColor: '#3182ce',
      boxShadow: '0px 1px 0px 0px #3182ce',
    },
  },
});

const SubscribeForm: React.FC<{
  setNextStep?: (step: STEP_NAME) => void;
}> = ({ setNextStep }) => {
  const {
    email,
    searchValue,
    showTextProviderInput,
    selectedProvider,
    isPolicyAgreed,
    policyError,
    providerError,
    emailError,
    isLoading,
    handleEmailChange,
    handleManualProviderChange,
    toggleManualProviderInput,
    handleSearchInputChange,
    handlePolicyChange,
    subscribe,
    getNewMenuOptions,
  } = useSubscription(setNextStep);

  return (
    <>
      <form onSubmit={(e) => e.preventDefault()}>
        <Stack className='!gap-y-6 md:!gap-y-8'>
          <FormControl variant='floating' isInvalid={Boolean(emailError)}>
            <Input
              name='email'
              variant='flushed'
              type='email'
              placeholder=''
              value={email}
              className='!text-[#475467]'
              onChange={handleEmailChange}
            />
            <FormLabel>Email</FormLabel>
            {emailError && <FormErrorMessage>{emailError}</FormErrorMessage>}
          </FormControl>
          <FormControl variant='floating' isInvalid={Boolean(providerError)}>
            <Stack spacing={3}>
              {!showTextProviderInput && (
                <Stack spacing={3}>
                  <input
                    type='text'
                    name='provider'
                    placeholder=''
                    readOnly
                    value={selectedProvider?.value}
                    className='hidden'
                  />
                  <FormLabel>Seleccione ISP</FormLabel>
                  <Menu isLazy matchWidth={true} flip={false}>
                    {({ isOpen }) => {
                      return (
                        <>
                          <MenuButton
                            as={StyledMenuButton}
                            value={selectedProvider?.value}
                            rightIcon={
                              isOpen ? (
                                <ChevronUpIcon boxSize={6} />
                              ) : (
                                <ChevronDownIcon boxSize={6} />
                              )
                            }
                          >
                            {Object.keys(selectedProvider)?.length > 0 && selectedProvider?.label}
                          </MenuButton>
                          <MenuList minWidth='240px'>
                            <MenuGroup>
                              <InputGroup>
                                <InputLeftElement>
                                  <SearchIcon ml={3} color='#7C828D' />
                                </InputLeftElement>
                                <Input
                                  mx={3}
                                  mb={3}
                                  pl={8}
                                  name='search'
                                  className='!text-[#475467]'
                                  placeholder='Buscar'
                                  value={searchValue}
                                  onBlur={(e) => e.target.focus()}
                                  onChange={handleSearchInputChange}
                                />
                              </InputGroup>
                            </MenuGroup>
                            <MenuOptionGroup>{getNewMenuOptions}</MenuOptionGroup>
                          </MenuList>
                        </>
                      );
                    }}
                  </Menu>
                </Stack>
              )}
              {showTextProviderInput && (
                <HStack spacing={3}>
                  <Input
                    name='manualSelectedProvider'
                    variant='flushed'
                    type='text'
                    placeholder='Escriba su propio ISP'
                    value={selectedProvider?.value}
                    className='!text-[#475467] '
                    onChange={handleManualProviderChange}
                  />
                  <IconButton
                    color='white'
                    backgroundColor='#1CA5CC'
                    size='sm'
                    className='hover:!bg-[#60c0db]'
                    aria-label='Delete input'
                    icon={<DeleteIcon />}
                    onClick={toggleManualProviderInput}
                  />
                </HStack>
              )}
            </Stack>
            {providerError && (
              <FormErrorMessage>{providerError}</FormErrorMessage>
            )}
          </FormControl>
          <FormControl isInvalid={Boolean(policyError)}>
            <StyledCheckbox
              className='text-[#475467] !text-[20px]'
              icon={<CustomIcon isChecked={isPolicyAgreed} />}
              isChecked={isPolicyAgreed}
              onChange={handlePolicyChange}
              isInvalid={Boolean(policyError)}
            >
              Acepto la{' '}
              <a
                target='_blank'
                href='/privacy-policy'
                className='text-[#475467] font-bold hover:underline'
              >
                política de privacidad
              </a>
            </StyledCheckbox>
            {policyError && <FormErrorMessage>{policyError}</FormErrorMessage>}
          </FormControl>
          <SubmitButton
            height='48px'
            type='submit'
            onClick={subscribe}
            isLoading={isLoading}
            loadingText='Submitting'
          >
            Enviar
          </SubmitButton>
        </Stack>
      </form>
    </>
  );
};

export default SubscribeForm;
